import { useEffect, useRef, useState } from 'react';
import { useViewportSize } from 'react-scale/hooks/use-viewport';
import { gsap } from 'react-scale/lib/gsap';
import Image from 'next/image';

import s from './cards.module.scss';
import { Card } from './card';
import { toVw } from 'react-scale/lib/util/math';
import Marquee from 'react-fast-marquee';
import cn from 'classnames';
import { useKeenSlider } from 'keen-slider/react';
import 'keen-slider/keen-slider.min.css';
import { useMedia } from 'react-use';

export const Cards = () => {
  const firstCardRef = useRef<HTMLDivElement>();
  const secondCardRef = useRef<HTMLDivElement>();
  const thirdCardRef = useRef<HTMLDivElement>();
  const scrollTimeline = useRef<GSAPTimeline>(null);
  const isDesktop = useMedia('(min-width: 1024px)');
  const [loaded, setLoaded] = useState(false);

  const [sliderRef] = useKeenSlider({
    slides: {
      origin: 'center',
      perView: 1.15,
      spacing: 22
    },
    created() {
      setLoaded(true);
    },
    breakpoints: {
      '(min-width: 640px)': {
        slides: {
          origin: 'center',
          perView: 1.2,
          spacing: 32
        }
      }
    }
  });

  useEffect(() => {
    if (!firstCardRef.current || !secondCardRef.current || !thirdCardRef.current || !isDesktop)
      return;

    const cleanUp = () => {
      if (!scrollTimeline.current) return;

      scrollTimeline.current.getChildren().map(t => {
        t.targets().forEach((target: HTMLElement) => gsap.set(target, { clearProps: 'all' }));
        t.kill();
      });

      scrollTimeline.current.scrollTrigger?.kill(true);
      scrollTimeline.current.kill();
    };

    gsap.to([firstCardRef.current, secondCardRef.current, thirdCardRef.current], {
      autoAlpha: 1,
      duration: 0.3
    });

    scrollTimeline.current = gsap
      .timeline({
        scrollTrigger: {
          scrub: 0.2,
          end: 'center center',
          trigger: firstCardRef.current,
          start: 'top bottom+=10%'
        }
      })
      .fromTo(
        firstCardRef.current,
        {
          rotate: '-2.52deg',
          xPercent: -50,
          yPercent: -50
        },
        {
          ease: 'none',
          rotate: '-5.52deg',
          x: toVw(-585),
          y: toVw(40)
        }
      )
      .fromTo(
        secondCardRef.current,
        {
          rotate: '2.52deg',
          xPercent: -50,
          yPercent: -50
        },
        {
          ease: 'none',
          rotate: '5.52deg',
          x: toVw(585),
          y: toVw(40)
        },
        '<'
      )
      .fromTo(
        thirdCardRef.current,
        { xPercent: -50, yPercent: -50 },
        {
          ease: 'none',
          y: 0
        },
        '<'
      );

    return cleanUp;
  }, [isDesktop]);

  return (
    <div className={s.section}>
      <div className={cn('hidden lg:block', s['seatbelt__wrapper'])}>
        <Seatbelt />
      </div>

      <div className="lg:hidden h-full relative">
        <Marquee
          gradient={false}
          direction="right"
          className={cn('!w-[120%]', s['seatbelt__wrapper'])}
        >
          <Seatbelt />
        </Marquee>

        <Marquee gradient={false} className={s['seatbelt__wrapper--second']}>
          <Seatbelt />
        </Marquee>
      </div>

      <div
        ref={sliderRef}
        className={cn(
          loaded ? 'opacity-100 delay-300' : 'opacity-0',
          'transition-opacity duration-300 keen-slider lg:!hidden !absolute top-[45%] -translate-y-1/2'
        )}
      >
        <Card
          className="keen-slider__slide"
          quote="I think that for this decade what we're really going to see is these tools proliferating. They're going to be everywhere and will be baked into every company. I think it's kind of like the internet transition. Today, we don't even talk about internet strategies. It's just so integral to every business. And I think that AI is going to be much the same."
          author={{
            image: {
              src: '/static/zeitgeist/2023/cards/greg-brockman.png',
              width: 308,
              height: 177
            },
            fullName: 'Greg Brockman',
            position: 'President & co-founder, OpenAI',
            event: 'TransformX October 2022'
          }}
        />
        <Card
          className="keen-slider__slide"
          quote="Stable diffusion is a generative search engine. By putting this in a pipeline at the right place and having human-in-the-loop interactions, and having these engines, it will allow us to have even better experiences that understand what we want."
          author={{
            image: {
              src: '/static/zeitgeist/2023/cards/emad-mostaque.png',
              width: 308,
              height: 177
            },
            fullName: 'Emad Mostaque',
            position: 'Founder and CEO, Stability AI',
            event: 'TransformX October 2022'
          }}
        />
        <Card
          className="keen-slider__slide"
          quote="I'm most excited about the ability for our models to start using tools out in the world. Giving them access to search engines and augmenting them with that knowledge as a resource so that instead of them having to memorize all of these facts, they can make reference to a live, updated, knowledge base. I think that is going to be super impactful."
          author={{
            image: {
              src: '/static/zeitgeist/2023/cards/aidan-gomez.png',
              width: 308,
              height: 177
            },
            fullName: 'Aidan Gomez',
            position: 'CEO, Cohere',
            event: 'TransformX October 2022'
          }}
        />
      </div>

      <div className={s.cards}>
        <Card
          ref={secondCardRef}
          quote="I'm most excited about the ability for our models to start using tools out in the world. Giving them access to knowledge bases, giving them access to search engines like Google or Bing and augmenting them with that knowledge as a resource so that instead of them having to memorize all of these facts, they can make reference to a live, updated, knowledge base. I think that's going to be super impactful."
          author={{
            image: {
              src: '/static/zeitgeist/2023/cards/aidan-gomez.png',
              width: 575,
              height: 297
            },
            fullName: 'Aidan Gomez',
            position: 'CEO, Cohere',
            event: 'TransformX October 2022'
          }}
        />
        <Card
          ref={firstCardRef}
          quote="One of the ways that I describe Stable Diffusion is as a generative search engine. You don't need to use image search anymore because you can just make the image. By putting this in a pipeline at the right place and having human-in-the-loop interactions like the work that you do at Scale AI, understanding how humans do that at scale, and having these engines, it will allow us to have even better experiences that understand what we want."
          author={{
            image: {
              src: '/static/zeitgeist/2023/cards/emad-mostaque.png',
              width: 575,
              height: 297
            },
            fullName: 'Emad Mostaque',
            position: 'Founder and CEO, Stability AI',
            event: 'TransformX October 2022'
          }}
        />
        <Card
          ref={thirdCardRef}
          quote="I think that for this decade, what we're really going to see is these tools just proliferating. They're going to be everywhere and will be baked into every company. I think it's kind of like the internet transition. If you're a company, what's your internet strategy? And here we are today, where we don’t even talk about internet strategies. It’s just so integral to every business. It's not a separate part of your business that you can pick or choose whether you're going to have it. And I think that AI is going to be much the same."
          author={{
            image: {
              src: '/static/zeitgeist/2023/cards/greg-brockman.png',
              width: 575,
              height: 297
            },
            fullName: 'Greg Brockman',
            position: 'President & co-founder, OpenAI',
            event: 'TransformX October 2022'
          }}
        />
      </div>
    </div>
  );
};

const Seatbelt = () => {
  const seatbelt = useRef<HTMLDivElement>();
  const scrollTimeline = useRef<GSAPTimeline>(null);
  const viewport = useViewportSize();

  useEffect(() => {
    if (!viewport.width || viewport.width < 1024) return;

    const cleanUp = () => {
      if (!scrollTimeline.current) return;

      scrollTimeline.current.getChildren().map(t => {
        t.targets().forEach((target: HTMLElement) => gsap.set(target, { clearProps: 'all' }));
        t.kill();
      });

      scrollTimeline.current.scrollTrigger?.kill(true);
      scrollTimeline.current.kill();
    };

    scrollTimeline.current = gsap
      .timeline({
        scrollTrigger: {
          scrub: 0.2,
          trigger: seatbelt.current
        }
      })
      .to(seatbelt.current, {
        x: -viewport.height * 2,
        ease: 'none'
      });

    return cleanUp;
  }, [viewport.height, viewport.width]);

  return (
    <div ref={seatbelt} className={s.seatbelt}>
      <Image
        width={123}
        height={123}
        src="/static/zeitgeist/2023/zeitgeist-logo-black.svg"
        className={s.logo}
        quality={100}
        alt="Zeitgeist logo"
      />
      <h5 className={s['seatbelt__text']}>ZEITGEIST</h5>
      <Image
        width={123}
        height={123}
        src="/static/zeitgeist/2023/zeitgeist-box-black.svg"
        className={s.logo}
        quality={100}
        alt="Zeitgeist box"
      />
      <h5 className={s['seatbelt__text']}>ZEITGEIST</h5>
      <Image
        width={123}
        height={123}
        src="/static/zeitgeist/2023/zeitgeist-logo-black.svg"
        className={s.logo}
        quality={100}
        alt="Zeitgeist logo"
      />
      <h5 className={s['seatbelt__text']}>ZEITGEIST</h5>
      <Image
        width={123}
        height={123}
        src="/static/zeitgeist/2023/zeitgeist-box-black.svg"
        className={s.logo}
        quality={100}
        alt="Zeitgeist box"
      />
      <h5 className={s['seatbelt__text']}>ZEITGEIST</h5>
      <Image
        width={123}
        height={123}
        src="/static/zeitgeist/2023/zeitgeist-logo-black.svg"
        className={s.logo}
        quality={100}
        alt="Zeitgeist logo"
      />
      <h5 className={s['seatbelt__text']}>ZEITGEIST</h5>
      <Image
        width={123}
        height={123}
        src="/static/zeitgeist/2023/zeitgeist-box-black.svg"
        className={s.logo}
        quality={100}
        alt="Zeitgeist box"
      />
      <h5 className={s['seatbelt__text']}>ZEITGEIST</h5>
      <Image
        width={123}
        height={123}
        src="/static/zeitgeist/2023/zeitgeist-logo-black.svg"
        className={s.logo}
        quality={100}
        alt="Zeitgeist logo"
      />
      <h5 className={s['seatbelt__text']}>ZEITGEIST</h5>
      <Image
        width={123}
        height={123}
        src="/static/zeitgeist/2023/zeitgeist-box-black.svg"
        className={s.logo}
        quality={100}
        alt="Zeitgeist box"
      />
      <h5 className={s['seatbelt__text']}>ZEITGEIST</h5>
    </div>
  );
};
